import LoadingButton from '@/common/components/base/button/loadingButton';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import {
  ONLY_NUMBERS_RULE,
  ONLY_NUMBERS_WITH_SEPARATORS_RULE,
} from '@/common/components/base/input/textInput/textInput.rules';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import MainLayout from '@/common/components/layouts/MainLayout';
import { requiredText } from '@/common/utils/rules/publicRules';
import { useAddAccessoryInitialVisitQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/addAccessoryInitialVisit/addAccessoryInitialVisitQueryOptions';
import { getInitialVisitAccessoryListQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getInitialVisitAccessoryList/getInitialVisitAccessoryListQueryOptions';
import { getInitialVisitSingleAccessoryQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getInitialVisitSingleAccessory/getInitialVisitSingleAccessoryQueryOptions';
import { PatchAccessoryInitialVisitQueryFormType } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchAccessoryInitialVisit/patchAccessoryInitialVisitQuery.type';
import { usePatchAccessoryInitialVisitQueryOptions } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchAccessoryInitialVisit/patchAccessoryInitialVisitQueryOptions';
import formatNumberWithCommas from '@/modules/adjusterReportModules/IncidentParticipants/utils/formatNumberWithCommas';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Flex } from 'styled-system/jsx';

const InitialVisitAccessoryFormPage = () => {
  return (
    <MainLayout title="افزودن قطعه" header="titleHeader">
      <InitialVisitAccessoryForm />
    </MainLayout>
  );
};

function InitialVisitAccessoryForm() {
  const { id } = useParams();
  const navigate = useNavigate();

  const location = useLocation();
  const partId = location.state as number;

  const methods = useForm<PatchAccessoryInitialVisitQueryFormType>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      model: '',
      price: '',
    },
  });

  const { refetch: getSubstitutionPartsList } = useQuery(
    getInitialVisitAccessoryListQueryOptions({
      id: Number(id) || 0,
    }),
  );

  const { data: singleSubstitutionPartData } = useQuery(
    getInitialVisitSingleAccessoryQueryOptions({
      part_id: partId,
    }),
  );

  const { mutate: addSubstitutionPart, isPending: postLoading } =
    useAddAccessoryInitialVisitQueryOptions();

  const { mutate: patchSubstitutionPart, isPending: patchLoading } =
    usePatchAccessoryInitialVisitQueryOptions();

  useEffect(() => {
    if (!singleSubstitutionPartData) return;

    methods.reset({
      name: singleSubstitutionPartData.name || '',
      model: singleSubstitutionPartData.model || '',
      price: formatNumberWithCommas(
        String(singleSubstitutionPartData.price || 0),
      ),
      number: Number(singleSubstitutionPartData.number || ''),
    });
  }, [singleSubstitutionPartData, methods]);

  const onSubmit = (data: PatchAccessoryInitialVisitQueryFormType) => {
    const payload = {
      ...data,
      price: Number(String(data.price ?? '0').replace(/,/g, '')),
    };

    const onSuccess = () => {
      getSubstitutionPartsList?.();
      methods.reset();
      navigate(-1);
    };
    if (!!partId) {
      patchSubstitutionPart(
        { id: +id!, part_id: partId!, data: payload },
        { onSuccess },
      );
    } else {
      addSubstitutionPart({ id: +id!, data: payload }, { onSuccess });
    }
  };

  return (
    <Box pt="24px">
      <TitleDescription>
        {partId ? 'ویرایش' : 'افزودن لوازم اضافی'}
      </TitleDescription>

      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={8} mt={24}>
          <TextInput
            label="نام قطعه"
            name="name"
            placeholder="نام قطعه را وارد کنید"
            rules={{ required: requiredText }}
          />

          <TextInput
            label="نوع و مدل"
            name="model"
            placeholder="نوع و مدل را وارد کنید"
            rules={{ required: requiredText }}
          />

          <NumberInputWithWords
            label="قیمت"
            name="price"
            placeholder="قیمت را وارد کنید"
            rules={{
              required: requiredText,
              ...ONLY_NUMBERS_WITH_SEPARATORS_RULE,
            }}
          />

          <TextInput
            label="تعداد قطعه"
            name="number"
            placeholder="تعداد قطعه را وارد کنید"
            rules={{ required: requiredText, ...ONLY_NUMBERS_RULE }}
          />

          <LoadingButton
            onClick={methods.handleSubmit(onSubmit)}
            loading={patchLoading || postLoading}
            fullWidth
          >
            ثبت و ادامه
          </LoadingButton>
        </Flex>
      </FormProvider>
    </Box>
  );
}

export default InitialVisitAccessoryFormPage;
